import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { AppHeader } from '@/shared/components/layout/AppHeader'
import { CompetitionCreateForm } from '@/features/survivor-admin/components/CompetitionCreateForm'
import { CompetitionSetupCard } from '@/features/survivor-admin/components/CompetitionSetupCard'
import { GameweekList } from '@/features/survivor-admin/components/GameweekList'
import { GameweekOpsCard } from '@/features/survivor-admin/components/GameweekOpsCard'
import { MatchResultsTable } from '@/features/survivor-admin/components/MatchResultsTable'
import { SurvivorScheduleCard } from '@/features/survivor-admin/components/SurvivorScheduleCard'
import {
  useAdminCompetitions,
  useSurvivorAdminGameweeks,
} from '@/features/survivor-admin/queries'
import styles from './sobrevivente.module.css'

type SurvivorAdminSearch = {
  competitionId?: string
  gameweekId?:    string
}

export const Route = createFileRoute('/_authenticated/admin/sobrevivente')({
  validateSearch: (search: Record<string, unknown>): SurvivorAdminSearch => {
    const result: SurvivorAdminSearch = {}
    if (typeof search.competitionId === 'string' && search.competitionId) {
      result.competitionId = search.competitionId
    }
    if (typeof search.gameweekId === 'string' && search.gameweekId) {
      result.gameweekId = search.gameweekId
    }
    return result
  },
  component: SurvivorAdminPage,
})

function SurvivorAdminPage() {
  const { competitionId, gameweekId } = Route.useSearch()
  const navigate = useNavigate()
  const [showCreate, setShowCreate] = useState(false)

  const competitions = useAdminCompetitions()
  const gameweeks    = useSurvivorAdminGameweeks(competitionId ?? null)

  const selected = competitions.data?.find((c) => c.id === competitionId) ?? null
  const selectedGw = gameweeks.data?.find((g) => g.id === gameweekId) ?? null

  function selectCompetition(id: string) {
    // Changing competition invalidates the gameweek selection.
    void navigate({
      to:      '/admin/sobrevivente',
      search:  { competitionId: id },
      replace: true,
    })
  }

  function selectGameweek(id: string) {
    void navigate({
      to:      '/admin/sobrevivente',
      search:  competitionId ? { competitionId, gameweekId: id } : { gameweekId: id },
      replace: true,
    })
  }

  return (
    <>
      <AppHeader title="Admin · Sobrevivente" />
      <div className={styles.page}>
        <div className={styles.topBar}>
          <Link to="/admin" className={styles.backLink}>
            <ChevronLeft size={14} />
            Escolher painel
          </Link>
          <p className={styles.scopeNote}>
            Este painel cobre apenas o Sobrevivente — nada de escalações, estatísticas de
            jogadores ou preços.
          </p>
        </div>

        {/* ── 1. Competição ───────────────────────────────────────────────── */}
        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Competição</h2>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => setShowCreate((v) => !v)}
            >
              {showCreate ? 'Cancelar' : '+ Nova competição'}
            </button>
          </div>

          {showCreate && (
            <div className={styles.stack}>
              <CompetitionCreateForm
                onCreated={(id) => { setShowCreate(false); selectCompetition(id) }}
              />
            </div>
          )}

          {competitions.isPending && <div className={styles.empty}>Carregando competições…</div>}
          {competitions.isError && (
            <div className={styles.empty}>
              Não foi possível carregar as competições.{' '}
              <button
                type="button"
                className={styles.retry}
                onClick={() => void competitions.refetch()}
              >
                Tentar novamente
              </button>
            </div>
          )}

          {competitions.data && competitions.data.length === 0 && !showCreate && (
            <div className={styles.empty}>
              Nenhuma competição ainda — crie a primeira.
            </div>
          )}

          {competitions.data && competitions.data.length > 0 && (
            <div className={styles.chips}>
              {competitions.data.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => selectCompetition(c.id)}
                  className={c.id === competitionId ? styles.chipSelected : styles.chip}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}

          {selected && (
            <div className={styles.stack}>
              <CompetitionSetupCard competition={selected} />
            </div>
          )}
        </section>

        {/* ── 2. Rodadas ──────────────────────────────────────────────────── */}
        {competitionId && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Rodadas</h2>
            <div className={styles.split}>
              <div className={styles.splitAside}>
                <GameweekList
                  competitionId={competitionId}
                  selectedId={gameweekId ?? null}
                  onSelect={selectGameweek}
                />
              </div>

              <div className={styles.splitMain}>
                {!selectedGw && (
                  <div className={styles.empty}>
                    Selecione uma rodada para bloquear, buscar resultados e finalizar.
                  </div>
                )}
                {selectedGw && (
                  <div className={styles.stack}>
                    <GameweekOpsCard gw={selectedGw} />
                    <MatchResultsTable
                      gameweekId={selectedGw.id}
                      isFinalized={selectedGw.status === 'FINALIZED'}
                    />
                    <SurvivorScheduleCard
                      gameweekId={selectedGw.id}
                      gameweekNumber={selectedGw.number}
                      firstMatchAt={selectedGw.firstMatchAt}
                      lastMatchEndsAt={selectedGw.lastMatchEndsAt}
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  )
}
